// Third-party
import React from 'react';
import Dropzone, { Accept, DropzoneOptions } from 'react-dropzone';
import classNames from 'classnames';

// Components
import { PrimaryLink } from '@/views/components/buttons';

// Utils
import { formatBytes, formatMessage, FormattedMessage } from '@/utils/i18n';

// Images
import { ReactComponent as CsvIcon } from '@/images/csv-icon.svg?react';

export type AvailableTemplateDownloads = 'standard' | 'tax' | 'standard_and_tax';

type Props = {
  onDrop?: DropzoneOptions['onDrop'];
  accept: Accept;
  maxSize: number;
  onDownloadTemplate: (isTaxTemplate?: boolean) => void;
  availableTemplateDownloads?: AvailableTemplateDownloads;
};

const UploadStep: React.FC<Props> = ({ onDrop, accept, maxSize, onDownloadTemplate, availableTemplateDownloads = 'standard' }) => {
  const renderTemplateDownloads = () => {
    switch (availableTemplateDownloads) {
      case 'standard_and_tax':
        return (
          <div className="file-uploader-reference">
            <FormattedMessage
              id="Modal.PortfolioUpload.StandardAndTaxTemplateReference"
              values={{
                standard: <PrimaryLink onClick={() => onDownloadTemplate(false)}>{formatMessage('Modal.PortfolioUpload.StandardTemplate')}</PrimaryLink>,
                tax: <PrimaryLink onClick={() => onDownloadTemplate(true)}>{formatMessage('Modal.PortfolioUpload.TaxAnalysisTemplate')}</PrimaryLink>,
              }}
            />
          </div>
        );

      case 'tax':
        return (
          <div className="file-uploader-reference">
            <FormattedMessage
              id="Modal.PortfolioUpload.TaxTemplateReference"
              values={{
                tax: <PrimaryLink onClick={() => onDownloadTemplate(true)}>{formatMessage('Modal.PortfolioUpload.TaxAnalysisTemplate')}</PrimaryLink>,
              }}
            />
          </div>
        );

      case 'standard':
      default:
        return (
          <div className="file-uploader-info">
            {formatMessage('Modal.PortfolioUpload.Info')}{' '}
            <PrimaryLink onClick={() => onDownloadTemplate(false)}>{formatMessage('Modal.PortfolioUpload.DownloadTemplate')}</PrimaryLink>
          </div>
        );
    }
  };

  return (
    <div className="file-uploader-upload-step">
      <Dropzone onDrop={onDrop} accept={accept} maxSize={maxSize} minSize={1} multiple={false} noClick noKeyboard>
        {({ getRootProps, getInputProps, open, isDragActive }) => (
          <div
            {...getRootProps()}
            className={classNames('file-uploader-dropzone', {
              'is-dragging': isDragActive,
            })}
          >
            <input {...getInputProps()} />

            <div className="file-uploader-dropzone-cta">
              <CsvIcon className="file-uploader-csv-icon" />
              <div className="d-flex align-items-center mt-2">
                <span className="me-2">{formatMessage('Modal.PortfolioUpload.DragDrop')}</span>
                <PrimaryLink onClick={open}>{formatMessage('Modal.PortfolioUpload.Upload')}</PrimaryLink>
              </div>
            </div>

            <span className="file-uploader-dropzone-limit">{formatMessage('Modal.PortfolioUpload.SizeLimit', { limit: formatBytes(maxSize) })}</span>
          </div>
        )}
      </Dropzone>

      {renderTemplateDownloads()}
    </div>
  );
};

export default UploadStep;
